import React, { useState, useEffect } from 'react';
import { modalPrimaryBtn, MODAL_BTN_ROW, modalCard } from '../modalButtons';
import ModalOverlay from './ModalOverlay';

function SceneController() {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('Touch');

  // 1. Manage 'modal-open' class on body
  useEffect(() => {
    if (showModal) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [showModal]);

  // 1.5 Opening info (button or I key) closes the settings dropdown. Its own
  // close-on-outside-click listener never sees the info click — the capture
  // handler below stops propagation — so both stayed open at once.
  useEffect(() => {
    if (showModal) document.getElementById('profile-dropdown')?.classList.remove('open');
  }, [showModal]);

  // 2. Disable pointer events on Three.js container
  useEffect(() => {
    const threeContainer = document.getElementById('three-container');
    if (threeContainer) {
      threeContainer.style.pointerEvents = showModal ? 'none' : 'auto';
    }
    return () => {
      if (threeContainer) threeContainer.style.pointerEvents = 'auto';
    };
  }, [showModal]);

  // 2.5 HARD BLOCKER: swallow *all* input while modal is open (desktop + mobile)
  useEffect(() => {
    if (!showModal) return;

    const stopAll = (e) => {
      // Allow interactions *inside* the modal content
      const modalContent = document.querySelector('.modal-content');
      if (modalContent && modalContent.contains(e.target)) return;

      // Block everything else
      try { e.preventDefault(); } catch {}
      try { e.stopPropagation(); } catch {}
      try { e.stopImmediatePropagation(); } catch {}
      return false;
    };

    const opts = { capture: true, passive: false };
    const types = [
      'touchstart','touchmove','touchend','touchcancel',
      'pointerdown','pointermove','pointerup','pointercancel',
      'mousedown','mousemove','mouseup',
      'click','dblclick','contextmenu','wheel'
    ];

    types.forEach(t => document.addEventListener(t, stopAll, opts));
    return () => { types.forEach(t => document.removeEventListener(t, stopAll, opts)); };
  }, [showModal]);

  // 3. Global dblclick interception with exclusion check
  useEffect(() => {
    const stopGlobalDblClick = (e) => {
      const modalOverlay = document.querySelector('.modal-overlay');
      // If the dblclick originates within the modal, stop it completely
      if (modalOverlay && modalOverlay.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // If modal is open, block dblclicks globally
      if (showModal) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (showModal) {
      document.addEventListener('dblclick', stopGlobalDblClick, true);
    }

    return () => {
      document.removeEventListener('dblclick', stopGlobalDblClick, true);
    };
  }, [showModal]);

  // 4. Global click listener for #info-button
  useEffect(() => {
    const handleClick = (e) => {
      const infoButton = document.getElementById('info-button');
      if (infoButton && (e.target === infoButton || infoButton.contains(e.target))) {
        e.preventDefault();
        e.stopPropagation();
        setShowModal(true);
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  // 6. I key toggles modal open/close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (window.__accountModalOpen) return;
      const targetTag = e.target && e.target.tagName;
      if (targetTag === 'INPUT' || targetTag === 'TEXTAREA' || (e.target && e.target.isContentEditable)) return;
      if (e.key.toLowerCase() === 'i') {
        e.preventDefault();
        e.stopPropagation();
        setShowModal((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Capturing isn't available to anonymous visitors following a public
  // share link — see SceneHandler.js's handleScreenshotClick.
  const isSharedView = !!sessionStorage.getItem('shareToken');

  // --- Tab content definition ---
  const tabContent = {
    Desktop: (
      <div>
        <p><b>Orbit center</b>: Double click</p>
        <p><b>Orbit</b>: Left mouse click</p>
        <p><b>Move</b>: Right mouse click</p>
        <p><b>Zoom</b>: Scroll</p>
        <p><b>Fly around</b>: W/A/S/D Keys</p>
        <p><b>Look around</b>: Up/Down/Left/Right Arrows</p>
        <p><b>Reset scroll</b>: Double click on scrollbar</p>
      </div>
    ),
    Touch: (
      <div>
        <p><b>Orbit center</b>: Double tap</p>
        <p><b>Orbit</b>: One finger drag</p>
        <p><b>Move</b>: Two finger drag</p>
        <p><b>Zoom</b>: Pinch</p>
        <p><b>Fly around</b>: W/A/S/D Keys</p>
        <p><b>Look around</b>: Up/Down/Left/Right Arrows</p>
        <p><b>Reset scroll</b>: Double tap on scrollbar</p>
      </div>
    ),
    'Hot-Keys': (
      <div>
        <p><b>Reset camera position</b>: H</p>
        <p><b>Boom Up</b>: Q</p>
        <p><b>Boom Down</b>: E</p>
        <p><b>Double fly speed</b>: Shift + W/A/S/D/Q/E</p>
        {!isSharedView && <p><b>Capture</b>: C</p>}
        <p><b>Info</b>: I</p>
        <p><b>Fullscreen</b>: F</p>
      </div>
    ),
  };

  return (
    <>
      {showModal && (
        <ModalOverlay
        >
          <div
            className="modal-content"
            style={{ ...modalCard, maxWidth: 420, textAlign: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >

            {/* --- Tab Buttons --- (same underline tabs as the Settings modal, QualityModal.js) */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #eee' }}>
              {['Desktop', 'Touch', 'Hot-Keys'].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  style={{
                    flex: 1, padding: '8px 0 10px', background: 'none', border: 'none', cursor: 'pointer',
                    borderBottom: activeTab === tab ? '2px solid #1a1a1a' : '2px solid transparent',
                    marginBottom: -1,
                    color: activeTab === tab ? '#1a1a1a' : '#888',
                    fontWeight: activeTab === tab ? 600 : 400,
                    fontSize: 13, fontFamily: 'Inter, Arial, sans-serif',
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* --- Tab Content --- */}
            <div
              style={{
                textAlign: 'left',
                fontSize: 13,
                lineHeight: 1.5,
                color: '#1a1a1a',
                fontFamily: 'Inter, Arial, sans-serif',
                minHeight: 156,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {tabContent[activeTab]}
            </div>

            {/* --- Footer Button --- */}
            <div style={MODAL_BTN_ROW}>
              <button type="button" onClick={() => setShowModal(false)} style={{ ...modalPrimaryBtn, width: '100%' }}>
                OK
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}

export default SceneController;
